import { Extension, mergeAttributes, Node } from "@tiptap/core";

const BACKGROUND_CLASS = "lh6-grey-background";
const BACKGROUND_BLOCK_CLASS = "lh6-background-block";

const BACKGROUND_STYLE_RE = /(?:^|;)\s*background(?:-color)?\s*:[^;]+/gi;

const hasBackgroundStyle = (element) => {
  const style = (element.getAttribute("style") || "").toLowerCase();
  if (!style) return false;
  if (!/(^|;)\s*background(?:-color)?\s*:/.test(style)) return false;
  return !/background(?:-color)?\s*:\s*(transparent|none)/.test(style);
};

const extractBackgroundStyle = (element) => {
  const style = element?.getAttribute?.("style") || "";
  return (style.match(BACKGROUND_STYLE_RE) || []).join("; ");
};

const shouldParseBackground = (element) => {
  if (!element) return false;
  if (element.classList?.contains(BACKGROUND_CLASS)) return true;
  if (element.getAttribute("data-lh-background") === "grey") return true;
  return hasBackgroundStyle(element);
};

const mergeClassName = (existingClass = "") => {
  const classes = String(existingClass)
    .split(/\s+/)
    .filter(Boolean)
    .filter(
      (className) =>
        className !== BACKGROUND_CLASS && className !== BACKGROUND_BLOCK_CLASS
    );

  return [BACKGROUND_BLOCK_CLASS, BACKGROUND_CLASS, ...classes].join(" ");
};

const GreyBackgroundExtension = Node.create({
  name: "greyBackground",

  group: "block",

  content: "block+",

  defining: true,

  addAttributes() {
    return {
      class: {
        default: "",
        parseHTML: (element) => element.getAttribute("class") || "",
        renderHTML: () => ({}),
      },
      style: {
        default: "",
        parseHTML: (element) => extractBackgroundStyle(element),
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [
      { tag: `div.${BACKGROUND_CLASS}` },
      { tag: `section.${BACKGROUND_CLASS}` },
      { tag: 'div[data-lh-background="grey"]' },
      { tag: 'section[data-lh-background="grey"]' },
      {
        tag: "div[style]",
        getAttrs: (element) => (shouldParseBackground(element) ? {} : false),
      },
      {
        tag: "section[style]",
        getAttrs: (element) => (shouldParseBackground(element) ? {} : false),
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const existingClass = HTMLAttributes.class || "";
    const existingStyle = HTMLAttributes.style || "";
    const isGreyBlock =
      existingClass.split(/\s+/).includes(BACKGROUND_CLASS) || !existingStyle;
    const className = isGreyBlock
      ? mergeClassName(existingClass)
      : [BACKGROUND_BLOCK_CLASS, existingClass].filter(Boolean).join(" ");
    const attrs = {
      class: className,
      "data-lh-background": isGreyBlock ? "grey" : "custom",
    };

    if (existingStyle) {
      attrs.style = existingStyle;
    }

    return [
      "div",
      mergeAttributes(attrs),
      0,
    ];
  },

  addCommands() {
    return {
      setGreyBackground:
        () =>
        ({ commands }) =>
          commands.wrapIn(this.name),
      unsetGreyBackground:
        () =>
        ({ commands }) =>
          commands.lift(this.name),
      toggleGreyBackground:
        () =>
        ({ commands }) =>
          commands.toggleWrap(this.name),
    };
  },
});

export const BackgroundStyleAttributesExtension = Extension.create({
  name: "backgroundStyleAttributes",

  addGlobalAttributes() {
    return [
      {
        types: [
          "paragraph",
          "heading",
          "blockquote",
          "bulletList",
          "orderedList",
          "listItem",
          "tableCell",
          "tableHeader",
        ],
        attributes: {
          backgroundStyle: {
            default: null,
            parseHTML: (element) => extractBackgroundStyle(element) || null,
            renderHTML: (attributes) => {
              if (!attributes.backgroundStyle) return {};
              return { style: attributes.backgroundStyle };
            },
          },
        },
      },
      {
        types: ["textStyle"],
        attributes: {
          textBackgroundStyle: {
            default: null,
            parseHTML: (element) => extractBackgroundStyle(element) || null,
            renderHTML: (attributes) => {
              if (!attributes.textBackgroundStyle) return {};
              return { style: attributes.textBackgroundStyle };
            },
          },
        },
      },
    ];
  },
});

export default GreyBackgroundExtension;
